// App.tsx — T-021. The router root.
//
// Renders the public marketing site at `/` and the locale-prefixed routes
// (`/de`, `/hu`, `/en`), the auth screens at `/login` and `/signup`, the
// authenticated app at `/app/*`, and a 404 catch-all.

import type { ReactElement } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import LegalPage from "./components/legal/LegalPage";
import { I18nProvider } from "./i18n";
import { detectLocale } from "./i18n/detectLocale";
import { AuthProvider } from "./services/auth";
import LandingPage from "./components/landing/LandingPage";
import { LoginPage } from "./components/auth/LoginPage";
import { SignupPage } from "./components/auth/SignupPage";
import { ForgotPasswordPage } from "./components/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./components/auth/ResetPasswordPage";
import { RequireAuth } from "./components/routing/RequireAuth";
import { LocaleLayout } from "./components/routing/LocaleLayout";
import { DefaultLocaleLayout } from "./components/routing/DefaultLocaleLayout";
import { NotFoundPage } from "./components/routing/NotFoundPage";
import { AppShell } from "./components/app/AppShell";
import { AdaptiveStatusBar } from "./components/app/AdaptiveStatusBar";
import { PwaUpdateToast } from "./components/app/PwaUpdateToast";
import { LevelEarnedToast } from "./components/app/LevelEarnedToast";
import { InstallPrompt } from "./components/app/InstallPrompt";

export default function App(): ReactElement {
  const initialLocale = detectLocale();
  return (
    <I18nProvider initial={initialLocale}>
      <AuthProvider>
        <BrowserRouter>
          <AdaptiveStatusBar />
          <Routes>
            <Route element={<DefaultLocaleLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/privacy" element={<LegalPage initialDocId="privacy" />} />
              <Route path="/terms" element={<LegalPage initialDocId="terms" />} />
              <Route path="/imprint" element={<LegalPage initialDocId="imprint" />} />
            </Route>
            {/* R-11 password reset: the unprefixed path is the canonical one
                (the PocketBase email template will point here once the
                orchestrator updates it; see run log). Registered BEFORE
                `:locale` so a request to `/forgot-password` does not get
                swallowed by the `:locale=forgot-password` fallback
                (LocaleLayout would render NotFoundPage for an unknown
                prefix). The locale-prefixed aliases are nested inside
                `:locale` below. */}
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path=":locale" element={<LocaleLayout />}>
              <Route index element={<LandingPage />} />
              <Route path="privacy" element={<LegalPage initialDocId="privacy" />} />
              <Route path="terms" element={<LegalPage initialDocId="terms" />} />
              <Route path="imprint" element={<LegalPage initialDocId="imprint" />} />
              {/* R-11: locale-prefixed aliases for the reset flow. A
                  shared `/hu/forgot-password` link renders in Hungarian
                  because LocaleLayout drives the i18n provider. */}
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
            </Route>
            {/* Legally required and linked from the footer, so these must resolve
                rather than fall through to the 404. */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route element={<RequireAuth />}>
              <Route path="/app" element={<Navigate to="/app/workout" replace />} />
              <Route path="/app/*" element={<AppShell />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <PwaUpdateToast />
          <LevelEarnedToast />
          <InstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
